/**
 * preferences.ts
 *
 * Typed localStorage module for user preferences.
 * All reads are wrapped in try/catch for private-browsing safety.
 *
 * Keys kept in sync with sound-engine.ts which uses 'pullit-sound-muted'
 * independently — this module is the canonical reader/writer for that key too.
 */

export type ThemePref = 'dark' | 'light' | 'system';
export type AnimationMode = 'none' | 'low' | 'all';

const KEYS = {
  theme: 'pullit-theme',
  animationMode: 'pullit-animation-mode',
  soundMuted: 'pullit-sound-muted',
} as const;

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

export function getThemePref(): ThemePref {
  try {
    const raw = localStorage.getItem(KEYS.theme);
    if (raw === 'dark' || raw === 'light' || raw === 'system') return raw;
  } catch {
    // private browsing / storage blocked
  }
  return 'dark';
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(KEYS.theme, pref);
  } catch {
    // ignore — private browsing or storage quota
  }
}

// ---------------------------------------------------------------------------
// Animation mode
// ---------------------------------------------------------------------------

export function getAnimationMode(): AnimationMode {
  try {
    const raw = localStorage.getItem(KEYS.animationMode);
    if (raw === 'none' || raw === 'low' || raw === 'all') return raw;
  } catch {
    // private browsing / storage blocked
  }
  return 'all';
}

export function setAnimationMode(mode: AnimationMode): void {
  try {
    localStorage.setItem(KEYS.animationMode, mode);
  } catch {
    // ignore — private browsing or storage quota
  }
  document.documentElement.setAttribute('data-animation-mode', mode);
}

// ---------------------------------------------------------------------------
// Sound muted
// ---------------------------------------------------------------------------

export function getSoundMuted(): boolean {
  try {
    return localStorage.getItem(KEYS.soundMuted) === 'true';
  } catch {
    return false;
  }
}

export function setSoundMuted(muted: boolean): void {
  try {
    localStorage.setItem(KEYS.soundMuted, String(muted));
  } catch {
    // ignore — private browsing or storage quota
  }
}
